import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDeleteOutline, MdLock, MdUnfoldLess, MdUnfoldMore, MdWarningAmber } from "react-icons/md";
import { NEW_FIELD_VALUES, blankLike, displayPath, getIn, humanizeKey, inferFieldType, isPlainObject, isTokenizableKey, listLeaves, pathToString, removeIn, setIn, typeOf } from "./contentUtils";

// Expand / collapse state and search shared by every group of one form.
const FoldContext = createContext({ openAll: null, version: 0, searching: false, large: false, query: "" });

// A value (any depth) matches the search when its key, full path or text
// does, or when one of its descendants does.
function subtreeMatches(value, parts, query, labelFor) {
  if (!query) return true;
  const key = parts[parts.length - 1];
  if (`${typeof key === "number" ? `[${key}]` : key} ${displayPath(parts)} ${labelFor?.(parts) || ""}`.toLowerCase().includes(query)) return true;
  if (isPlainObject(value)) return Object.entries(value).some(([k, child]) => subtreeMatches(child, [...parts, k], query, labelFor));
  if (Array.isArray(value)) return value.some((child, index) => subtreeMatches(child, [...parts, index], query, labelFor));
  return String(value ?? "").toLowerCase().includes(query);
}
// Children shown under a group: all of them when the group itself matches.
const visibleChildren = (entries, parts, fold, labelFor) => {
  if (!fold.query) return entries;
  const own = `${displayPath(parts)} ${labelFor?.(parts) || ""}`.toLowerCase().includes(fold.query);
  return own ? entries : entries.filter(([key, child]) => subtreeMatches(child, [...parts, key], fold.query, labelFor));
};

// A form generated from ANY JSON value: objects become sections, arrays
// repeatable lists (cards for arrays of objects), scalars an input chosen
// from the key and the value (text / long text / URL / image / number /
// switch / null). Every edit produces a new document through onChange;
// unknown keys and value types are never dropped.
//
// lockedPaths: dotted paths that may be edited but not removed / retyped
// (e.g. Weekly Income built-in texts). sourceDocument: the source language,
// shown as a hint under each text. defaultDocument: built-in default texts
// (a "Default text" reset link appears on a changed field). labelFor(parts):
// a friendly label, else the humanized key. tokenFor(parts): the {{token}}.

// role="switch" instead of a native checkbox: mdbreact's global mdb.css
// hides/blocks native checkboxes across front_elakay.
function Toggle({ checked, onChange, label }) {
  return <button type="button" role="switch" aria-checked={Boolean(checked)} aria-label={label} className={`eck-switch${checked ? " is-on" : ""}`} onClick={() => onChange(!checked)}><span className="eck-switch__knob" /></button>;
}

// Keeps the typed text while it is not a valid number (never stores NaN).
function NumberInput({ id, value, onChange, t }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { if (Number(text) !== value) setText(String(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const invalid = text.trim() === "" || !Number.isFinite(Number(text));
  return <>
    <input id={id} type="number" className={`eck-input${invalid ? " is-invalid" : ""}`} value={text} onChange={(e) => { setText(e.target.value); const n = Number(e.target.value); if (e.target.value.trim() !== "" && Number.isFinite(n)) onChange(n); }} />
    {invalid && <span className="eck-field__error">{t.invalidNumber}</span>}
  </>;
}

function ScalarInput({ id, fieldType, value, onChange, t, placeholder }) {
  if (fieldType === "boolean") return <Toggle checked={value} onChange={onChange} label={id} />;
  if (fieldType === "number") return <NumberInput id={id} value={value} onChange={onChange} t={t} />;
  if (fieldType === "null") {
    return <div className="eck-null">
      <span className="eck-badge">null</span>
      <input id={id} className="eck-input" placeholder={t.nullPlaceholder} value="" onChange={(e) => onChange(e.target.value)} />
    </div>;
  }
  if (fieldType === "textarea") return <textarea id={id} className="eck-input eck-input--area" rows={Math.min(8, Math.max(2, Math.ceil(String(value).length / 70)))} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
  const input = <input id={id} type={fieldType === "url" || fieldType === "image" ? "url" : "text"} className="eck-input" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
  if (fieldType !== "image") return input;
  return <div className="eck-image-field">
    {input}
    {/^https?:\/\//i.test(String(value)) && <img src={value} alt="" className="eck-image-field__preview" loading="lazy" />}
  </div>;
}

// keyPattern (optional): a stricter key rule of one email type. Without it
// any key is accepted; one a token cannot express only gets a warning.
function AddField({ onAdd, existingKeys, t, keyPattern }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [type, setType] = useState("text");
  const error = !key ? "" : keyPattern && !keyPattern.test(key) ? t.keyInvalid : existingKeys.includes(key) ? t.keyExists : "";
  const warning = key && !error && !isTokenizableKey(key) ? t.keyNotTokenizable : "";
  if (!open) return <button type="button" className="eck-link-btn" onClick={() => setOpen(true)}><MdAdd aria-hidden="true" /> {t.addField}</button>;
  return <div className="eck-add-field">
    <input className="eck-input" aria-label={t.newKey} placeholder={t.newKey} value={key} onChange={(e) => setKey(keyPattern ? e.target.value.trim() : e.target.value)} />
    <select className="eck-input" aria-label={t.fieldType} value={type} onChange={(e) => setType(e.target.value)}>
      {Object.keys(NEW_FIELD_VALUES).map((name) => <option key={name} value={name}>{t.types?.[name] || name}</option>)}
    </select>
    <button type="button" className="eck-btn eck-btn--primary" disabled={!key || Boolean(error)} onClick={() => { onAdd(key, NEW_FIELD_VALUES[type]); setKey(""); setOpen(false); }}>{t.add}</button>
    <button type="button" className="eck-btn" onClick={() => { setOpen(false); setKey(""); }}>{t.cancel}</button>
    {error && <span className="eck-field__error">{error}</span>}
    {warning && <span className="eck-field__warning"><MdWarningAmber aria-hidden="true" /> {warning}</span>}
  </div>;
}

function Node({ document, parts, onChange, t, lockedPaths, sourceDocument, defaultDocument, labelFor, tokenFor, onTokenClick, depth, keyPattern }) {
  const fold = useContext(FoldContext);
  const value = getIn(document, parts);
  const key = parts[parts.length - 1];
  const path = pathToString(parts);
  const locked = lockedPaths?.has(path);
  const fieldType = inferFieldType(key, value);
  const label = labelFor?.(parts) || (typeof key === "number" ? `${t.item} ${key + 1}` : humanizeKey(key));
  const id = `eck-f-${path.replace(/[^A-Za-z0-9_]/g, "-")}`;
  const parentIsArray = parts.length > 1 && Array.isArray(getIn(document, parts.slice(0, -1)));
  const update = (next) => onChange(setIn(document, parts, next));
  const remove = () => onChange(removeIn(document, parts));
  const removeButton = !locked && parts.length > 0 && !parentIsArray && <button type="button" className="eck-icon-btn" aria-label={`${t.remove} — ${path}`} title={t.remove} onClick={() => { if (window.confirm(t.confirmRemoveField.replace("{key}", path))) remove(); }}><MdDeleteOutline aria-hidden="true" /></button>;

  // Groups (objects, lists) fold; a large document starts with its top
  // groups closed, Expand / Collapse all and a search open/close them all.
  const groupOpen = fold.searching || (fold.openAll === null ? !(fold.large && depth === 0) : fold.openAll);
  const groupHead = (extra) => <summary className="eck-group__head">
    <span className="eck-group__title">{label}</span>{extra}
    <span className="eck-path" title={path}>{displayPath(parts)}</span>
    {locked && <MdLock aria-label={t.builtIn} />}{removeButton}
  </summary>;

  if (fieldType === "object") {
    const keys = Object.keys(value);
    return <details key={`g-${fold.version}`} open={groupOpen} className={`eck-group eck-group--d${Math.min(depth, 3)}`}>
      {groupHead(<span className="eck-badge">{keys.length}</span>)}
      <div className="eck-group__body">
        {!keys.length && <p className="eck-hint">{t.emptyObject}</p>}
        {visibleChildren(keys.map((k) => [k, value[k]]), parts, fold, labelFor).map(([child]) => <Node key={child} document={document} parts={[...parts, child]} onChange={onChange} t={t} lockedPaths={lockedPaths} sourceDocument={sourceDocument} defaultDocument={defaultDocument} labelFor={labelFor} tokenFor={tokenFor} onTokenClick={onTokenClick} depth={depth + 1} keyPattern={keyPattern} />)}
        <AddField existingKeys={keys} t={t} keyPattern={keyPattern} onAdd={(newKey, initial) => update({ ...value, [newKey]: initial })} />
      </div>
    </details>;
  }

  if (fieldType === "array") {
    const template = value.find((item) => item !== undefined);
    const move = (index, delta) => { const next = [...value]; const [item] = next.splice(index, 1); next.splice(index + delta, 0, item); update(next); };
    return <details key={`g-${fold.version}`} open={groupOpen} className={`eck-group eck-group--list eck-group--d${Math.min(depth, 3)}`}>
      {groupHead(<span className="eck-badge">{value.length}</span>)}
      <div className="eck-group__body">
      {visibleChildren(value.map((item, index) => [index, item]), parts, fold, labelFor).map(([index, item]) => <div key={index} className={`eck-list-item${isPlainObject(item) ? " is-card" : ""}`}>
        <div className="eck-list-item__body">
          <Node document={document} parts={[...parts, index]} onChange={onChange} t={t} lockedPaths={lockedPaths} sourceDocument={sourceDocument} defaultDocument={defaultDocument} labelFor={labelFor} tokenFor={tokenFor} onTokenClick={onTokenClick} depth={depth + 1} keyPattern={keyPattern} />
        </div>
        <div className="eck-list-item__actions">
          <button type="button" className="eck-icon-btn" aria-label={`${t.moveUp} — ${path}[${index}]`} disabled={index === 0} onClick={() => move(index, -1)}><MdArrowUpward aria-hidden="true" /></button>
          <button type="button" className="eck-icon-btn" aria-label={`${t.moveDown} — ${path}[${index}]`} disabled={index === value.length - 1} onClick={() => move(index, 1)}><MdArrowDownward aria-hidden="true" /></button>
          <button type="button" className="eck-icon-btn" aria-label={`${t.removeItem} — ${path}[${index}]`} onClick={() => update(value.filter((_, i) => i !== index))}><MdDeleteOutline aria-hidden="true" /></button>
        </div>
      </div>)}
      <button type="button" className="eck-link-btn" onClick={() => update([...value, template === undefined ? "" : blankLike(template)])}><MdAdd aria-hidden="true" /> {t.addItem}</button>
      </div>
    </details>;
  }

  const source = sourceDocument ? getIn(sourceDocument, parts) : undefined;
  const builtInDefault = defaultDocument ? getIn(defaultDocument, parts) : undefined;
  const token = tokenFor ? tokenFor(parts) : null;
  const typeChanged = source !== undefined && source !== null && value !== null && typeOf(source) !== typeOf(value);
  return <div className={`eck-field${parentIsArray ? " eck-field--item" : ""}`}>
    {!parentIsArray && <div className="eck-field__head">
      <label htmlFor={id}>{label}</label>
      {locked && <MdLock className="eck-lock" aria-label={t.builtIn} title={t.builtIn} />}
      {token && <button type="button" className="eck-token" title={t.tokenHint} onClick={() => onTokenClick?.(token)}>{token}</button>}
      {tokenFor && !token && <span className="eck-field__warning" title={t.untokenizableKey}><MdWarningAmber aria-hidden="true" /> {t.noToken}</span>}
      <span className="eck-path" title={path}>{displayPath(parts)}</span>
      <span className="eck-field__type">{t.types?.[fieldType] || fieldType}</span>
      {typeof builtInDefault === "string" && value !== builtInDefault && <button type="button" className="eck-link-btn eck-link-btn--small" aria-label={`${t.resetDefault} — ${label}`} onClick={() => update(builtInDefault)}>{t.resetDefault}</button>}
      {value !== null && fieldType !== "null" && !locked && <button type="button" className="eck-link-btn eck-link-btn--small" onClick={() => update(null)}>{t.setNull}</button>}
      {removeButton}
    </div>}
    <ScalarInput id={id} fieldType={fieldType} value={value} onChange={update} t={t} placeholder={typeof source === "string" ? source : undefined} />
    {typeof source === "string" && source && source !== value && <div className="eck-source-hint"><span>{t.sourceText}</span> {source}</div>}
    {typeChanged && <div className="eck-field__error">{t.typeChanged.replace("{expected}", typeOf(source)).replace("{actual}", typeOf(value))}</div>}
  </div>;
}

// Search keeps the top-level entries whose key, label, full paths or values
// match, and opens their groups.
export default function DynamicJsonForm({ value, onChange, t, lockedPaths, sourceDocument, defaultDocument, labelFor, tokenFor, onTokenClick, filter = "", keyPattern }) {
  const [openAll, setOpenAll] = useState(null);
  const [version, setVersion] = useState(0);
  const leafCount = useMemo(() => (isPlainObject(value) ? listLeaves(value).length : 0), [value]);
  if (!isPlainObject(value)) return <p className="eck-alert">{t.rootMustBeObject}</p>;
  const query = filter.trim().toLowerCase();
  const keys = Object.keys(value).filter((key) => subtreeMatches(value[key], [key], query, labelFor));
  const setAll = (open) => { setOpenAll(open); setVersion((v) => v + 1); };
  const hasGroups = Object.values(value).some((child) => child && typeof child === "object");
  return <FoldContext.Provider value={{ openAll, version, searching: Boolean(query), large: leafCount > 40, query }}>
    <div className="eck-form">
      {hasGroups && <div className="eck-toolbar">
        <button type="button" className="eck-btn" onClick={() => setAll(true)}><MdUnfoldMore aria-hidden="true" /> {t.expandAll}</button>
        <button type="button" className="eck-btn" onClick={() => setAll(false)}><MdUnfoldLess aria-hidden="true" /> {t.collapseAll}</button>
        <span className="eck-hint">{t.leafCount.replace("{count}", leafCount)}</span>
      </div>}
      {keys.map((key) => <Node key={key} document={value} parts={[key]} onChange={onChange} t={t} lockedPaths={lockedPaths} sourceDocument={sourceDocument} defaultDocument={defaultDocument} labelFor={labelFor} tokenFor={tokenFor} onTokenClick={onTokenClick} depth={0} keyPattern={keyPattern} />)}
      {query && !keys.length && <p className="eck-hint">{t.noMatch}</p>}
      {!query && <AddField existingKeys={Object.keys(value)} t={t} keyPattern={keyPattern} onAdd={(newKey, initial) => onChange({ ...value, [newKey]: initial })} />}
    </div>
  </FoldContext.Provider>;
}

